/* Predictive search (§4.11): products + collections as you type,
   debounced, fetched through the Section Rendering API and swapped into
   the search drawer's results region. Keyboard navigable: arrows move
   through results (combobox pattern, focus stays in the input), Enter
   follows the active result, ESC is handled by the drawer. */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('#PredictiveResults');
    if (!this.input || !this.results) return;
    this.activeIndex = -1;

    this.input.addEventListener('input', () => {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.search(), 250);
    });

    this.input.addEventListener('keydown', (event) => {
      const options = this.options();
      if (options.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.setActive(Math.min(this.activeIndex + 1, options.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.setActive(Math.max(this.activeIndex - 1, -1));
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex].click();
      }
    });
  }

  options() {
    return Array.from(this.results.querySelectorAll('[role="option"]'));
  }

  setActive(index) {
    const options = this.options();
    this.activeIndex = index;
    options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    if (index >= 0) {
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  async search() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.results.innerHTML = '';
      this.activeIndex = -1;
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }
    const url =
      `${window.Dotto.routes.predictive_search_url}` +
      `?q=${encodeURIComponent(term)}` +
      `&resources[type]=product,collection` +
      `&resources[limit]=6` +
      `&section_id=predictive-search`;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('#PredictiveResultsInner');
      // Ignore stale responses that finish after the input changed again
      if (this.input.value.trim() !== term) return;
      this.results.innerHTML = inner ? inner.outerHTML : '';
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.options().forEach((option, i) => (option.id = `PredictiveOption-${i}`));
      this.input.setAttribute('aria-expanded', String(Boolean(inner)));
    } catch (error) {
      /* network hiccup: keep previous results */
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
